import { useSuspenseQueries } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useGetPositionQuoteByPositionProductId } from '../clients/CustomRequestPositionClient';

export function getPositionQuoteQueryKey(positionProductId: string | null) {
    return ['positionQuote', positionProductId];
}

type ConfigOptions = { fetchOnce?: boolean };

const usePositionQuoteQueryOptionsFactory = ({ fetchOnce = false }: ConfigOptions = {}) => {
    const { getPositionQuoteByPositionProductId } = useGetPositionQuoteByPositionProductId();
    return (positionProductId: string | null) => ({
        queryKey: getPositionQuoteQueryKey(positionProductId),
        queryFn: async () => {
            if (!positionProductId) {
                return null;
            }
            try {
                return await getPositionQuoteByPositionProductId({ positionProductId });
            } catch (error) {
                // since the query might return a 404, we need to prevent the query to throw an error for that case
                if (isAxiosError(error) && error.response?.status === 404) {
                    return null;
                }
                throw error;
            }
        },
        refetchOnWindowFocus: false,
        ...(fetchOnce && { staleTime: Infinity, cacheTime: Infinity }),
    });
};

export const usePositionQuotes = (positionProductIds: string[], configOptions?: ConfigOptions) => {
    const createQueryOptions = usePositionQuoteQueryOptionsFactory(configOptions);
    return useSuspenseQueries({ queries: positionProductIds.map(createQueryOptions) });
};
